import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useRaffleSummary, useScrapers } from '../../api/queries';
import { Ic } from '../../components/Ic';
import { Seg } from '../../components/Seg';
import { Empty } from '../../components/Empty';
import { StatusDot, StatusPill } from '../../components/StatusDot';
import { deriveScraperState, STATE_META, type ScraperState } from '../../lib/scraperState';
import { formatDuration, formatInterval, relTime } from '../../lib/time';
import { ScraperCard } from './ScraperCard';
import { ScraperActions } from './ScraperActions';
import { ScraperDetailModal, isDetailTab } from './ScraperDetailModal';
import { usePresence } from '../../components/usePresence';

const VIEW_OPTIONS = [
  { value: 'grid' as const, label: 'Grid', icon: Ic.grid },
  { value: 'list' as const, label: 'List', icon: Ic.list },
];

const STATE_ORDER: ScraperState[] = ['running', 'healthy', 'degraded', 'down', 'paused'];

export function ScrapersView() {
  const navigate = useNavigate();
  const { name, tab } = useParams();
  const [params, setParams] = useSearchParams();

  const { data: scrapers, isLoading, error } = useScrapers();
  const { data: summary } = useRaffleSummary(12);

  const mode = params.get('view') === 'list' ? 'list' : 'grid';
  const filter = params.get('state') ?? 'all';

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === 'all' || value === 'grid') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const counts = STATE_ORDER.map((state) => ({
    state,
    n: scrapers?.filter((s) => deriveScraperState(s) === state).length ?? 0,
  })).filter((c) => c.n > 0);

  const visible = (scrapers ?? []).filter(
    (s) => filter === 'all' || deriveScraperState(s) === filter,
  );

  const statsFor = (scraperName: string) =>
    summary?.by_scraper[scraperName] ?? { total: 0, today: 0 };

  const selected = name ? scrapers?.find((s) => s.name === name) : undefined;
  const detailTab = isDetailTab(tab) ? tab : 'metrics';
  // Latch scraper + tab so the modal can animate out after the URL clears.
  const detail = usePresence(selected ? { scraper: selected, tab: detailTab } : undefined);

  return (
    <div className="view-pad">
      <div className="sec-head">
        <h2>Sources</h2>
        <span className="desc">
          {scrapers ? `${scrapers.length} registered` : error ? 'unavailable' : 'loading…'}
        </span>
        <span className="state-counts">
          {counts.map(({ state, n }) => (
            <button
              key={state}
              className={`btn ghost sm state-count ${filter === 'all' || filter === state ? '' : 'dim'}`}
              aria-pressed={filter === state}
              onClick={() => setParam('state', filter === state ? 'all' : state)}
              title={`Show only ${STATE_META[state].label}`}
            >
              <span className="sw" style={{ background: STATE_META[state].dot }} />
              <span className="n">{n}</span>
            </button>
          ))}
        </span>
        <span className="spacer" />
        {filter !== 'all' && (
          <button className="btn ghost sm" onClick={() => setParam('state', 'all')}>
            Clear filter <Ic.x className="ico" />
          </button>
        )}
        <Seg
          value={mode}
          onChange={(next) => setParam('view', next)}
          options={VIEW_OPTIONS}
          ariaLabel="Layout"
        />
      </div>

      {isLoading && <div className="muted loading-delayed">Loading scrapers…</div>}

      {/* Distinct from the empty state on purpose: "no scrapers registered"
          when the API is simply unreachable would read as "your scrapers are
          gone", which is the opposite of what happened. */}
      {error && (
        <Empty
          icon={Ic.alert}
          title="Could not reach the API"
          detail={error instanceof Error ? error.message : String(error)}
        />
      )}

      {!isLoading && !error && visible.length === 0 && (
        <Empty
          icon={Ic.box}
          title={filter === 'all' ? 'No scrapers registered' : `No ${filter} scrapers`}
          detail={filter === 'all' ? 'Add one from the Add Scraper page.' : undefined}
        />
      )}

      {mode === 'grid' && visible.length > 0 && (
        <div className="scraper-grid">
          {visible.map((s) => {
            const stats = statsFor(s.name);
            return (
              <ScraperCard
                key={s.name}
                scraper={s}
                found={stats.total}
                today={stats.today}
                onOpen={() => navigate(`/scrapers/${encodeURIComponent(s.name)}`)}
              />
            );
          })}
        </div>
      )}

      {mode === 'list' && visible.length > 0 && (
        <div className="pcard">
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Scraper</th>
                  <th>Status</th>
                  <th>Health</th>
                  <th>Found · today</th>
                  <th>Fails</th>
                  <th>Interval</th>
                  <th>Avg</th>
                  <th>Next / last</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((s) => {
                  const state = deriveScraperState(s);
                  const stats = statsFor(s.name);
                  return (
                    <tr
                      key={s.name}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/scrapers/${encodeURIComponent(s.name)}`)}
                    >
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <StatusDot state={state} />
                          {s.name}
                        </span>
                      </td>
                      <td>
                        <StatusPill state={state} />
                      </td>
                      <td className="mono-sm">{s.health_score}</td>
                      <td className="mono-sm">
                        {stats.total} · {stats.today}
                      </td>
                      <td className="mono-sm" style={s.consecutive_failures ? { color: 'var(--err)' } : undefined}>
                        {s.consecutive_failures || '—'}
                      </td>
                      <td className="mono-sm">{formatInterval(s.interval_seconds)}</td>
                      <td className="mono-sm">{formatDuration(s.avg_execution_time)}</td>
                      <td className="mono-sm">
                        {s.enabled && s.next_run ? relTime(s.next_run) : relTime(s.last_run)}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <ScraperActions scraper={s} />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail.item && (
        <ScraperDetailModal {...detail.item} open={detail.open} onExited={detail.onExited} />
      )}
      {/* A link to a scraper that no longer exists shouldn't look like a hang. */}
      {name && !selected && !isLoading && (
        <Empty icon={Ic.alert} title={`No scraper named "${name}"`} detail="It may have been removed." />
      )}
    </div>
  );
}
